/**
 * PrivateKeyReveal.jsx — Affichage et export des clés privées (wallets GENERATED).
 *
 * États machine couverts :
 *   KEY_OTP_REQUEST — Sélection du wallet + envoi du code OTP
 *   KEY_OTP_VERIFY  — Saisie OTP (6 cases individuelles) + mot de passe
 *   KEY_DISPLAY     — Affichage 12 mots floutés + copie + téléchargement keystore
 *
 * Règles de sécurité :
 *   - keyMaterial vit UNIQUEMENT dans le reducer (React memory) — jamais dans ce composant
 *   - Lockout après 5 tentatives OTP → RESET (géré dans le reducer)
 *   - Timer 120s → KEY_TIMER_EXPIRED → keyMaterial = null
 *   - Re-flou auto après 30s d'inactivité
 *
 * Règles DA :
 *   §20  — zéro littéral couleur hors theme.js
 *   §12 ter — zéro px en dur
 *   §18 ter — au moins 1 breakpoint responsive
 *   §12 undecies — casse grammaticale
 *   §14  — zéro MUI
 *   §8.7 — zéro Redux
 */

import React, { useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  WalletBody,
  WalletFooter,
  FormGroup,
  FormLabel,
  FormInput,
  FormInputWrap,
  PasswordToggle,
  InlineError,
  InlineWarning,
  PrimaryBtn,
  SecondaryBtn,
  BackLink,
  AlertBox,
  AlertText,
  OtpGrid,
  OtpCell,
  MnemonicGrid,
  MnemonicWord,
  MnemonicIndex,
  MnemonicWordText,
  CountdownBadge,
  StateIconWrap,
  StateTitle,
  StateMessage,
} from '../styles/EarniWalletModal.styles';
import {COLORS, FONTS, RADII, SPACE, media, FONT_SIZE, FONT_WEIGHT} from '../../ui/theme';
import { ACTION_TYPES }             from '../hooks/useWalletStateMachine';
import { usePrivateKeyExport }       from '../hooks/usePrivateKeyExport';
import { shortAddress } from '../../../utils/format';
/* styled2 supprimé — seul styled de '../../ui/theme' via les composants styles est utilisé */

/* ─── Styled-components locaux ──────────────────────────────────────────────── */

const StepIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACE.sm};
  margin-bottom: ${SPACE.md};
`;

const StepDot = styled.div`
  width: 0.5rem;
  height: 0.5rem;
  border-radius: ${RADII.circle};
  background: ${(p) => (p.$active ? COLORS.accent : p.$done ? COLORS.cta : COLORS.line)};
  transition: background 0.2s;
`;

const StepLabel = styled.span`
  font-family: ${FONTS.body};
  font-size: 0.8125rem;
  color: ${COLORS.muted};
`;

const RevealButton = styled.button`
  background: ${COLORS.accentSoft};
  border: 0.0625rem solid ${COLORS.accent14};
  border-radius: ${RADII.button};
  color: ${COLORS.accent};
  font-family: ${FONTS.body};
  font-size: 0.8125rem;
  font-weight: ${FONT_WEIGHT.semibold};
  padding: ${SPACE.xs} ${SPACE.md};
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: ${COLORS.accent14};
  }
`;

const ActionRow = styled.div`
  display: flex;
  gap: ${SPACE.sm};
  flex-wrap: wrap;

  ${media.sm`
    flex-direction: column;
  `}
`;

const CopySuccessMsg = styled.span`
  font-family: ${FONTS.body};
  font-size: ${FONT_SIZE.xs};
  color: ${COLORS.cta};
  font-weight: ${FONT_WEIGHT.semibold};
`;

const PasswordToggleLocal = styled.button`
  position: absolute;
  right: ${SPACE.md};
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: ${COLORS.muted};
  display: flex;
  align-items: center;
  padding: 0;
  &:hover { color: ${COLORS.ink}; }
`;

const WalletSelectRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE.xs};
`;

/* ─── Icônes SVG inline ──────────────────────────────────────────────────────── */
function IconLock() {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={COLORS.accent} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
  );
}

function IconKey() {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={COLORS.accent} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>
    </svg>
  );
}

function IconWarning() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
      <line x1="12" y1="9" x2="12" y2="13"/>
      <line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
  );
}

function IconCheck() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  );
}

function IconEye({ hidden }) {
  return hidden ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19M1 1l22 22"/>
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  );
}

function IconArrow() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="15 18 9 12 15 6"/>
    </svg>
  );
}

function IconDownload() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
      <polyline points="7 10 12 15 17 10"/>
      <line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
  );
}

/* ─── Composant ─────────────────────────────────────────────────────────────── */

/**
 * PrivateKeyReveal
 *
 * @param {object}      props
 * @param {function}    props.dispatch          - dispatch de useWalletStateMachine
 * @param {string}      props.apiBaseUrl         - URL de base de l'API
 * @param {object|null} props.keyMaterial        - { keystore, mnemonic, address } (KEY_DISPLAY uniquement)
 * @param {string}      props.currentState       - état courant de la machine
 * @param {Array}       props.linkedWallets       - liste des wallets liés (pour sélectionner le wallet GENERATED)
 */
export function PrivateKeyReveal({
  dispatch,
  apiBaseUrl,
  keyMaterial,
  currentState,
  linkedWallets = [],
}) {
  const { t } = useTranslation();
  /* Filtre les wallets GENERATED disponibles */
  const generatedWallets = linkedWallets.filter((w) => w.walletType === 'GENERATED');

  /* Détermine l'étape active pour l'indicateur */
  const stepIndex =
    currentState === 'KEY_OTP_REQUEST' ? 0
    : currentState === 'KEY_OTP_VERIFY' ? 1
    : currentState === 'KEY_DISPLAY'    ? 2
    : 0;

  /* ── Hook métier ──────────────────────────────────────────────────────────── */
  const {
    walletId,
    setWalletId,
    otpLoading,
    otpError,
    handleRequestOtp,

    otpCode,
    setOtpCode,
    exportPassword,
    setExportPassword,
    verifyLoading,
    verifyError,
    handleVerifyOtp,

    countdown,
    isMnemonicBlurred,
    revealMnemonic,
    blurMnemonic,
    handleCopyMnemonic,
    handleDownloadKeystore,
    handleKeysSaved,
    copySuccess,
  } = usePrivateKeyExport({ dispatch, apiBaseUrl, keyMaterial, currentState });

  /* ── Références OTP (pour auto-focus) ──────────────────────────────────── */
  const otpRefs = [
    useRef(null), useRef(null), useRef(null),
    useRef(null), useRef(null), useRef(null),
  ];

  /* ── OTP : décompose `otpCode` (string) en tableau de 6 chars ─────────────────
     NB : ne PAS utiliser `padEnd(6, '')` — padEnd avec une chaîne vide ne pad rien,
     donc `''.padEnd(6,'').split('')` === [] → 0 case rendue (bug). On construit donc
     toujours un tableau de 6 entrées (char réel ou '' si absent). */
  const otpChars = Array.from({ length: 6 }, (_, i) => otpCode[i] ?? '');

  const handleOtpChange = useCallback((index, value) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const chars  = Array.from({ length: 6 }, (_, i) => otpCode[i] ?? '');
    chars[index] = digit;
    setOtpCode(chars.join('').replace(/\s/g, ''));
    /* Auto-focus suivant */
    if (digit && index < 5) {
      otpRefs[index + 1]?.current?.focus();
    }
  }, [otpCode, setOtpCode, otpRefs]);

  const handleOtpKeyDown = useCallback((index, e) => {
    if (e.key === 'Backspace') {
      if (!otpChars[index] && index > 0) {
        /* Efface la case précédente et focus dessus */
        const chars = Array.from({ length: 6 }, (_, i) => otpCode[i] ?? '');
        chars[index - 1] = '';
        setOtpCode(chars.join('').replace(/\s/g, ''));
        otpRefs[index - 1]?.current?.focus();
      } else {
        const chars = Array.from({ length: 6 }, (_, i) => otpCode[i] ?? '');
        chars[index] = '';
        setOtpCode(chars.join('').replace(/\s/g, ''));
      }
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      otpRefs[index - 1]?.current?.focus();
    }
    if (e.key === 'ArrowRight' && index < 5) {
      otpRefs[index + 1]?.current?.focus();
    }
  }, [otpCode, otpChars, setOtpCode, otpRefs]);

  /* Pâte collée dans n'importe quelle case OTP */
  const handleOtpPaste = useCallback((e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    setOtpCode(pasted);
    /* Focus sur la dernière case remplie */
    const lastFilled = Math.min(pasted.length, 5);
    otpRefs[lastFilled]?.current?.focus();
  }, [setOtpCode, otpRefs]);

  /* ── Visibilité mot de passe ────────────────────────────────────────────── */
  const [showPassword, setShowPassword] = React.useState(false);

  /* ── Retour Step 2 → Step 1 ─────────────────────────────────────────────── */
  const handleBackFromVerify = useCallback(() => {
    dispatch({ type: ACTION_TYPES.BACK });
  }, [dispatch]);

  /* ── Mots de la phrase mnémotechnique ───────────────────────────────────── */
  const mnemonicWords = (keyMaterial?.mnemonic ?? '').split(' ').filter(Boolean);

  /* ── Formatage adresse courte (keystore download) ───────────────────────── */
  const keystoreSuffix = keyMaterial?.address
    ? `${keyMaterial.address.slice(0, 6)}...${keyMaterial.address.slice(-4)}`
    : 'wallet';

  /* ─────────────────────────────────────────────────────────────────────────
     RENDU — KEY_OTP_REQUEST (Step 1 : demande d'envoi OTP)
     ───────────────────────────────────────────────────────────────────────── */
  if (currentState === 'KEY_OTP_REQUEST') {
    return (
      <>
        <WalletBody>
          {/* Indicateur d'étape */}
          <StepIndicator>
            <StepDot $active />
            <StepDot />
            <StepDot />
            <StepLabel>{t('KeyExportStep1')}</StepLabel>
          </StepIndicator>

          {/* Icône */}
          <StateIconWrap style={{ paddingTop: SPACE.md, paddingBottom: SPACE.sm }}>
            <IconLock />
            <StateTitle>{t('ExportRecoveryKeys')}</StateTitle>
            <StateMessage>
              {t('VerificationCodeSentNote')}
            </StateMessage>
          </StateIconWrap>

          {/* Avertissement sécurité */}
          <AlertBox $type="warning" role="alert">
            <IconWarning />
            <AlertText $type="warning">
              {t('NeverShareRecoveryWarning')}
            </AlertText>
          </AlertBox>

          {/* Sélection du wallet GENERATED si plusieurs */}
          {generatedWallets.length > 1 && (
            <FormGroup>
              <FormLabel htmlFor="ewm-export-wallet">{t('WalletToExport')}</FormLabel>
              <WalletSelectRow>
                {generatedWallets.map((w) => {
                  const short = shortAddress(w.address);
                  return (
                    <label
                      key={w.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: SPACE.sm,
                        cursor: 'pointer',
                        fontFamily: FONTS.body,
                        fontSize: '0.875rem',
                        color: COLORS.ink,
                      }}
                    >
                      <input
                        type="radio"
                        name="ewm-export-wallet"
                        value={w.id}
                        checked={walletId === w.id}
                        onChange={() => setWalletId(w.id)}
                        style={{ accentColor: COLORS.accent }}
                      />
                      {short} {w.isPrimary ? t('PrimaryParen') : ''}
                    </label>
                  );
                })}
              </WalletSelectRow>
            </FormGroup>
          )}

          {/* Auto-sélectionner si un seul wallet GENERATED */}
          {generatedWallets.length === 1 && !walletId && (
            <FormGroup>
              <FormLabel>{t('SelectedWallet')}</FormLabel>
              <FormInput
                type="text"
                value={shortAddress(generatedWallets[0].address)}
                readOnly
                style={{ cursor: 'default', background: COLORS.bg }}
              />
            </FormGroup>
          )}

          {/* Erreur */}
          {otpError && (
            <InlineError role="alert">{otpError}</InlineError>
          )}
        </WalletBody>

        <WalletFooter>
          <PrimaryBtn
            type="button"
            onClick={() => {
              /* Auto-sélectionner si un seul wallet GENERATED */
              const targetId = walletId ?? (generatedWallets.length === 1 ? generatedWallets[0].id : null);
              handleRequestOtp(targetId);
            }}
            disabled={otpLoading || (generatedWallets.length > 1 && !walletId)}
            aria-busy={otpLoading}
          >
            {otpLoading ? t('Sending') : t('SendCode')}
          </PrimaryBtn>
        </WalletFooter>
      </>
    );
  }

  /* ─────────────────────────────────────────────────────────────────────────
     RENDU — KEY_OTP_VERIFY (Step 2 : saisie OTP + mot de passe)
     ───────────────────────────────────────────────────────────────────────── */
  if (currentState === 'KEY_OTP_VERIFY') {
    return (
      <>
        <WalletBody>
          {/* Indicateur d'étape */}
          <StepIndicator>
            <StepDot $done />
            <StepDot $active />
            <StepDot />
            <StepLabel>{t('KeyExportStep2')}</StepLabel>
          </StepIndicator>

          <BackLink
            type="button"
            onClick={handleBackFromVerify}
            aria-label={t('BackToSendingCode')}
          >
            <IconArrow /> {t('BackAction')}
          </BackLink>

          <StateMessage style={{ marginBottom: SPACE.sm }}>
            {t('EnterOtpCodeNote')}
          </StateMessage>

          {/* 6 cases OTP */}
          <FormGroup>
            <FormLabel>{t('VerificationCodeLabel')}</FormLabel>
            <OtpGrid
              role="group"
              aria-label={t('OtpCodeGroupLabel')}
              onPaste={handleOtpPaste}
            >
              {otpChars.map((char, i) => (
                <OtpCell
                  key={i}
                  ref={otpRefs[i]}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={char}
                  onChange={(e) => handleOtpChange(i, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(i, e)}
                  aria-label={t('OtpDigitLabel', { index: i + 1 })}
                  autoComplete="one-time-code"
                  disabled={verifyLoading}
                  $hasError={!!verifyError}
                />
              ))}
            </OtpGrid>
          </FormGroup>

          {/* Mot de passe */}
          <FormGroup>
            <FormLabel htmlFor="ewm-export-pwd">{t('AccountPassword')}</FormLabel>
            <FormInputWrap>
              <FormInput
                id="ewm-export-pwd"
                type={showPassword ? 'text' : 'password'}
                value={exportPassword}
                onChange={(e) => setExportPassword(e.target.value)}
                placeholder={t('YourPasswordPlaceholder')}
                autoComplete="current-password"
                disabled={verifyLoading}
                $hasError={!!verifyError}
                style={{ paddingRight: '2.5rem' }}
                onKeyDown={(e) => { if (e.key === 'Enter') handleVerifyOtp(); }}
              />
              <PasswordToggleLocal
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? t('Hide') : t('Show')}
                tabIndex={-1}
              >
                <IconEye hidden={showPassword} />
              </PasswordToggleLocal>
            </FormInputWrap>
          </FormGroup>

          {/* Erreur inline */}
          {verifyError && (
            <InlineError role="alert">{verifyError}</InlineError>
          )}
        </WalletBody>

        <WalletFooter>
          <PrimaryBtn
            type="button"
            onClick={handleVerifyOtp}
            disabled={verifyLoading || otpCode.replace(/\s/g, '').length < 6 || !exportPassword}
            aria-busy={verifyLoading}
          >
            {verifyLoading ? t('Verifying') : t('VerifyAndAccessKeys')}
          </PrimaryBtn>
        </WalletFooter>
      </>
    );
  }

  /* ─────────────────────────────────────────────────────────────────────────
     RENDU — KEY_DISPLAY (Step 3 : affichage des clés)
     ───────────────────────────────────────────────────────────────────────── */
  if (currentState === 'KEY_DISPLAY' && keyMaterial) {
    return (
      <>
        <WalletBody>
          {/* Indicateur d'étape */}
          <StepIndicator>
            <StepDot $done />
            <StepDot $done />
            <StepDot $active />
            <StepLabel>{t('KeyExportStep3')}</StepLabel>
          </StepIndicator>

          {/* Compte à rebours */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: SPACE.sm }}>
            <CountdownBadge aria-live="polite" aria-atomic="true">
              {t('AutoClosingIn', { countdown })}
            </CountdownBadge>
          </div>

          {/* Avertissement de sécurité */}
          <AlertBox $type="error" role="alert">
            <IconWarning />
            <AlertText $type="error">
              <strong>Never share</strong> your recovery phrase or your keystore.
              Anyone who accesses it controls your funds permanently.
            </AlertText>
          </AlertBox>

          {/* Grille des 12 mots mnémotechniques */}
          <FormGroup>
            <FormLabel>
              {t('RecoveryPhrase12Words')}
            </FormLabel>

            <MnemonicGrid
              $blurred={isMnemonicBlurred}
              aria-label={t('RecoveryPhraseAriaLabel')}
              aria-hidden={isMnemonicBlurred ? 'true' : 'false'}
            >
              {mnemonicWords.map((word, i) => (
                <MnemonicWord key={i}>
                  <MnemonicIndex>{i + 1}.</MnemonicIndex>
                  <MnemonicWordText>{word}</MnemonicWordText>
                </MnemonicWord>
              ))}
              {/* Remplissage si moins de 12 mots (ne devrait pas arriver) */}
              {Array.from({ length: Math.max(0, 12 - mnemonicWords.length) }).map((_, i) => (
                <MnemonicWord key={`empty-${i}`}>
                  <MnemonicIndex>{mnemonicWords.length + i + 1}.</MnemonicIndex>
                  <MnemonicWordText>—</MnemonicWordText>
                </MnemonicWord>
              ))}
            </MnemonicGrid>

            {/* Bouton Révéler / Masquer */}
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: SPACE.sm, gap: SPACE.sm }}>
              {isMnemonicBlurred ? (
                <RevealButton
                  type="button"
                  onClick={revealMnemonic}
                  aria-label={t('RevealRecoveryPhraseAria')}
                >
                  {t('RevealPhrase')}
                </RevealButton>
              ) : (
                <RevealButton
                  type="button"
                  onClick={blurMnemonic}
                  aria-label={t('HideRecoveryPhraseAria')}
                >
                  {t('Hide')}
                </RevealButton>
              )}
            </div>
          </FormGroup>

          {/* Actions copie + téléchargement */}
          <ActionRow>
            {/* Copier la phrase */}
            <SecondaryBtn
              type="button"
              onClick={handleCopyMnemonic}
              disabled={isMnemonicBlurred}
              style={{ flex: 1 }}
              aria-label={t('CopyRecoveryPhraseAria')}
            >
              {copySuccess ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: SPACE.xs, justifyContent: 'center' }}>
                  <IconCheck /> {t('CopiedExclaim')}
                </span>
              ) : (
                t('CopyPhrase')
              )}
            </SecondaryBtn>

            {/* Télécharger keystore JSON */}
            <SecondaryBtn
              type="button"
              onClick={handleDownloadKeystore}
              style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: SPACE.xs }}
              aria-label={t('DownloadKeystoreAria', { filename: `keystore-${keystoreSuffix}.json` })}
            >
              <IconDownload /> keystore-{keystoreSuffix}.json
            </SecondaryBtn>
          </ActionRow>

          <InlineWarning style={{ textAlign: 'center', marginTop: SPACE.xs }}>
            {t('WindowAutoCloseNote', { countdown })}
          </InlineWarning>
        </WalletBody>

        <WalletFooter>
          <PrimaryBtn
            type="button"
            onClick={handleKeysSaved}
            aria-label={t('IHaveSavedMyKeys')}
          >
            {t('IHaveSavedMyKeys')}
          </PrimaryBtn>
        </WalletFooter>
      </>
    );
  }

  /* Fallback (état inattendu) */
  return null;
}

export default PrivateKeyReveal;
